import { isEmpty } from "@/main/services/utils";

import { RenderPartInformationProps, RenderQuoteItemInformationProps } from "./quoteProcurementPartInfo";
import { IRenderSupplierInventoryInformation } from "./quoteProcurementPartInfoColumn";

const ONLY_SEPARATORS_REGEX = /^[\s\-/[\](),.:|]*$/;
const GROUP_REGEX = /([[(])([^[\]()]*)([\])])/g;
const HAS_GROUP_REGEX = /[[\]()]/;

function hasContent(value: string): boolean {
	return !ONLY_SEPARATORS_REGEX.test(value);
}

function removeEmptySegments(content: string): string | null {
	const segments = content.split("/");
	const filledSegments = segments.filter(hasContent);

	if (filledSegments.length === 0) return null;
	if (filledSegments.length === segments.length) return content;

	return filledSegments.map((segment) => segment.trim()).join(" / ");
}

export function sanitizePartInfoText(info: string | null | undefined): string {
	if (isEmpty(info)) return "";

	const withoutEmptyGroups = info.replace(GROUP_REGEX, (_match, open: string, content: string, close: string) => {
		const cleanedContent = removeEmptySegments(content);

		return cleanedContent === null ? "" : `${open}${cleanedContent}${close}`;
	});

	const cleaned = HAS_GROUP_REGEX.test(withoutEmptyGroups)
		? withoutEmptyGroups
		: (removeEmptySegments(withoutEmptyGroups) ?? "");

	const sanitized = cleaned.replace(/\s{2,}/g, " ").trim();

	return hasContent(sanitized) ? sanitized : "";
}

export function getQuoteItemInformationText({
	quoteItemDescription,
	quoteItemPartNumber,
}: RenderQuoteItemInformationProps) {
	if (quoteItemDescription && quoteItemPartNumber) {
		return `${quoteItemDescription} - ${quoteItemPartNumber}`;
	} else if (quoteItemDescription) {
		return quoteItemDescription;
	} else if (quoteItemPartNumber) {
		return quoteItemPartNumber;
	}

	return "";
}

export function getRenderPartInformationText({ partName, partPosition }: RenderPartInformationProps) {
	let partInfo: string = "";

	if (partName) {
		partInfo = partName;

		if (partPosition) {
			partInfo += ` - ${partPosition}`;
		}

		return partInfo;
	}

	if (partPosition) {
		return partPosition;
	}

	return "";
}

export function getPartTypeAndBrandText(
	partType: string | undefined,
	partBrand: string | undefined,
	withoutConcat: boolean,
	_partTypeQuoteItem: string | undefined,
) {
	if (partType) {
		let partTypeAndBrand: string = "";
		if (partBrand) {
			partTypeAndBrand += `(${partBrand})`;
		}

		if (!withoutConcat) {
			partTypeAndBrand = `${partTypeAndBrand} `;
		}

		return partTypeAndBrand;
	}
}

export function getRenderSupplierInventoryInformationText({
	supplierInventoryPartTitle,
	partBrand,
	partType,
	showLineSupplierInfo,
	partTypeQuoteItem,
}: IRenderSupplierInventoryInformation) {
	let supplierInventoryInfo: string = "";

	if (showLineSupplierInfo) {
		if (showLineSupplierInfo?.info === "all") {
			if (supplierInventoryPartTitle) {
				supplierInventoryInfo = supplierInventoryPartTitle;

				if (partType) {
					supplierInventoryInfo += " - " + getPartTypeAndBrandText(partType, partBrand, true, partTypeQuoteItem);
				}

				return supplierInventoryInfo;
			}
			return getPartTypeAndBrandText(partType, partBrand, true, partTypeQuoteItem);
		} else if (showLineSupplierInfo?.info === "partType") {
			return getPartTypeAndBrandText(partType, partBrand, true, partTypeQuoteItem);
		}
	}

	return "";
}
